import React from "react";
import {
  Plane, BedDouble, ShieldCheck, FileText, MapPin, Car,
  ClipboardList, MessageCircle, CheckCircle2, FileIcon,
  Briefcase, Users, Home,
} from "lucide-react";
import { exampleFileUrl } from "@/lib/countries";

/* Unified preview card with A4 portrait body area.
   Renders: dark header (icon + title + subtitle) + A4 body
   Body shows: uploaded image/PDF (object-contain) OR a stylized default mockup OR a generic placeholder */

const TITLE_RULES = [
  { match: /no\s*objection|noc\b|employer.*letter/i, icon: Briefcase, body: NocBody },
  { match: /sponsor\s*letter|sponsor/i, icon: Users, body: SponsorBody },
  { match: /invitation\s*letter|host\s*invitation/i, icon: Home, body: InvitationBody },
  { match: /flight|airline|ticket|pnr/i, icon: Plane, body: FlightBody },
  { match: /hotel|booking|accommodation|reservation(?! .*car| .*rental)/i, icon: BedDouble, body: HotelBody },
  { match: /insurance|coverage|certificate/i, icon: ShieldCheck, body: InsuranceBody },
  { match: /cover\s*letter|letter/i, icon: FileText, body: CoverBody },
  { match: /itinerary|day-?by-?day|plan/i, icon: MapPin, body: ItineraryBody },
  { match: /car|rental|vehicle/i, icon: Car, body: CarBody },
  { match: /visa|application|form/i, icon: ClipboardList, body: VisaBody },
  { match: /whatsapp|support|24\/?7/i, icon: MessageCircle, body: SupportBody },
];

function matchRule(title) {
  return TITLE_RULES.find((r) => r.match.test(title || ""));
}

function isPdfPath(p) { return /\.pdf(\?|$)/i.test(p || ""); }

function UploadedBody({ url, scale, title }) {
  const pdf = isPdfPath(url);
  const src = exampleFileUrl(url);
  if (pdf) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[#f8fafc] p-2">
        <FileIcon className="w-8 h-8 text-[#94a3b8]" />
        <div className="mt-1 text-[10px] uppercase tracking-widest text-[#64748b] font-bold">PDF document</div>
      </div>
    );
  }
  return (
    <div className="w-full h-full bg-[#f8fafc] grid place-items-center overflow-hidden">
      <img
        src={src}
        alt={title ? `${title} sample preview` : "Schengen visa document sample preview"}
        draggable={false}
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", pointerEvents: "none" }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    </div>
  );
}

function GenericBody({ title }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-[#f8fafc] p-3 text-center">
      <FileText className="w-7 h-7 text-[#94a3b8]" />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#64748b] font-bold line-clamp-2">{title}</div>
    </div>
  );
}

/* ---- Default mockup bodies (A4 portrait, scale-aware) ---- */

function FlightBody({ scale }) {
  return (
    <div className={`w-full h-full bg-white p-3 flex flex-col`} style={{ fontSize: scale }}>
      <div className="text-[8px] uppercase tracking-widest text-[#64748b]">Boarding pass</div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-center">
          <div className="font-display font-black text-[#0f1b2d]" style={{ fontSize: `calc(${scale} * 2.4)` }}>DXB</div>
          <div className="text-[#64748b]">14:20</div>
        </div>
        <Plane className="text-[#2563eb] flex-shrink-0" style={{ width: `calc(${scale} * 2)`, height: `calc(${scale} * 2)` }} strokeWidth={2.2} />
        <div className="text-center">
          <div className="font-display font-black text-[#0f1b2d]" style={{ fontSize: `calc(${scale} * 2.4)` }}>CDG</div>
          <div className="text-[#64748b]">19:35</div>
        </div>
      </div>
      <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
        <Row label="Passenger" value="A. AL-RASHIDI" />
        <Row label="Flight" value="EK 073" />
        <Row label="Date" value="15 SEP 2026" />
        <Row label="PNR" value="HQ4X2P" />
      </div>
      <div className="mt-auto pt-2">
        <Badge>Confirmed</Badge>
      </div>
    </div>
  );
}

function HotelBody({ scale }) {
  return (
    <div className="w-full h-full bg-white flex flex-col" style={{ fontSize: scale }}>
      <div className="h-1/3 bg-gradient-to-br from-[#2563eb] to-[#60a5fa] grid place-items-center">
        <BedDouble className="text-white/90" style={{ width: `calc(${scale} * 2.6)`, height: `calc(${scale} * 2.6)` }} />
      </div>
      <div className="flex-1 p-3 flex flex-col">
        <div className="font-display font-bold text-[#0f1b2d]" style={{ fontSize: `calc(${scale} * 1.3)` }}>Hôtel Le Marais</div>
        <div className="text-[#64748b] mt-0.5">12 Rue de Rivoli, Paris</div>
        <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
          <Row label="Check-in" value="15 SEP 2026" />
          <Row label="Check-out" value="22 SEP 2026" />
          <Row label="Ref" value="BKG-2845-AX12" mono />
        </div>
        <div className="mt-auto pt-2">
          <Badge>Confirmed</Badge>
        </div>
      </div>
    </div>
  );
}

function InsuranceBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="text-[8px] uppercase tracking-widest text-[#64748b] text-center">Certificate of Insurance</div>
      <div className="mt-3 text-center">
        <div className="text-[#64748b] uppercase tracking-wider">Coverage</div>
        <div className="font-display font-black text-[#2563eb]" style={{ fontSize: `calc(${scale} * 3)` }}>€30,000</div>
        <div className="text-[#64748b]">Schengen Area · 30 days</div>
      </div>
      <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
        <Row label="Insured" value="A. AL-RASHIDI" />
        <Row label="Policy" value="IN-09823" mono />
        <Row label="Valid from" value="15 SEP 2026" />
        <Row label="Valid to" value="15 OCT 2026" />
      </div>
      <div className="mt-auto pt-2 grid place-items-center">
        <ShieldCheck className="text-emerald-600" style={{ width: `calc(${scale} * 1.6)`, height: `calc(${scale} * 1.6)` }} />
      </div>
    </div>
  );
}

function CoverBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="text-right">
        <div className="text-[#0f1b2d] font-semibold">Ahmed Al-Rashidi</div>
        <div className="text-[#64748b]">Riyadh, KSA</div>
      </div>
      <div className="mt-3 text-[#0f1b2d] font-semibold">To: The Visa Officer</div>
      <div className="text-[#64748b]">French Consulate</div>
      <div className="mt-3 space-y-1">
        <Line w="100%" />
        <Line w="92%" />
        <Line w="96%" />
        <Line w="88%" />
        <Line w="94%" />
        <Line w="70%" />
      </div>
      <div className="mt-auto pt-2 text-[#0f1b2d] italic">Sincerely, A. Al-Rashidi</div>
    </div>
  );
}

function ItineraryBody({ scale }) {
  const days = [
    { d: "Day 1", c: "Paris", a: "Louvre" },
    { d: "Day 2", c: "Paris", a: "Eiffel Tower" },
    { d: "Day 3", c: "Versailles", a: "Palace" },
    { d: "Day 4", c: "Lyon", a: "Old town" },
  ];
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="text-[8px] uppercase tracking-widest text-[#64748b]">Itinerary · 7 nights</div>
      <div className="mt-2 flex-1">
        {days.map((d, i, arr) => (
          <div key={i} className="flex gap-2">
            <div className="flex flex-col items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2563eb] flex-shrink-0 mt-1" />
              {i < arr.length - 1 && <div className="w-0.5 flex-1 bg-[#e2e8f0]" />}
            </div>
            <div className="mb-2 min-w-0 flex-1">
              <div className="font-semibold text-[#0f1b2d]">{d.d} · {d.c}</div>
              <div className="text-[#64748b]">{d.a}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-auto pt-2 flex items-center gap-1 text-[#64748b]">
        <MapPin className="text-[#2563eb]" style={{ width: `calc(${scale} * 1.1)`, height: `calc(${scale} * 1.1)` }} /> 6 cities
      </div>
    </div>
  );
}

function CarBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="text-[8px] uppercase tracking-widest text-[#64748b]">Hertz Rent-a-Car</div>
      <div className="mt-3 flex items-center justify-between">
        <div>
          <div className="text-[#64748b] uppercase tracking-wider">Vehicle</div>
          <div className="font-display font-bold text-[#0f1b2d]" style={{ fontSize: `calc(${scale} * 1.3)` }}>VW Golf</div>
          <div className="text-[#64748b]">Automatic</div>
        </div>
        <Car className="text-[#2563eb]" style={{ width: `calc(${scale} * 2.4)`, height: `calc(${scale} * 2.4)` }} />
      </div>
      <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
        <Row label="Conf" value="HZ-9X4T2" mono />
        <Row label="Pickup" value="CDG · 15 SEP" />
        <Row label="Drop-off" value="CDG · 22 SEP" />
      </div>
      <div className="mt-auto pt-2"><Badge>Confirmed</Badge></div>
    </div>
  );
}

function VisaBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="text-[8px] uppercase tracking-widest text-[#64748b] text-center">Schengen Visa Application</div>
      <div className="mt-2 grid grid-cols-3 gap-2 items-center">
        <div className="col-span-2 space-y-1">
          <Line w="80%" />
          <Line w="65%" />
          <Line w="90%" />
        </div>
        <div className="aspect-[3/4] bg-[#f1f5f9] border border-dashed border-[#cbd5e1] grid place-items-center text-[#94a3b8] text-[8px]">PHOTO</div>
      </div>
      <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
        <Row label="Name" value="A. AL-RASHIDI" />
        <Row label="DOB" value="12 MAR 1990" />
        <Row label="Passport" value="P0934128" mono />
        <Row label="Type" value="C — Short stay" />
      </div>
      <div className="mt-auto pt-2"><Badge>Completed</Badge></div>
    </div>
  );
}

function SupportBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col items-center justify-center text-center" style={{ fontSize: scale }}>
      <MessageCircle className="text-[#2563eb]" style={{ width: `calc(${scale} * 3)`, height: `calc(${scale} * 3)` }} />
      <div className="mt-2 font-display font-bold text-[#0f1b2d]" style={{ fontSize: `calc(${scale} * 1.4)` }}>24/7 WhatsApp Support</div>
      <div className="mt-1 text-[#64748b]">Reply within 5 minutes</div>
      <div className="mt-3"><Badge>Online</Badge></div>
    </div>
  );
}

function NocBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="font-display font-bold text-[#0f1b2d] text-center" style={{ fontSize: `calc(${scale} * 1.2)` }}>Al-Rashidi Trading Co.</div>
      <div className="text-[#64748b] text-center" style={{ fontSize: `calc(${scale} * 0.8)` }}>Riyadh, KSA</div>
      <div className="mt-3 text-[#0f1b2d] font-semibold">To: The Visa Officer</div>
      <div className="text-[#64748b]">German Consulate</div>
      <div className="mt-3 space-y-1">
        <Line w="100%" />
        <Line w="95%" />
        <Line w="98%" />
        <Line w="80%" />
      </div>
      <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
        <Row label="Employee" value="A. AL-RASHIDI" />
        <Row label="Position" value="Sr. Accountant" />
        <Row label="Since" value="15 MAR 2019" />
        <Row label="Travel" value="15–22 SEP 2026" />
      </div>
      <div className="mt-auto pt-2"><Badge>Authorized</Badge></div>
    </div>
  );
}

function SponsorBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="text-right">
        <div className="text-[#0f1b2d] font-semibold">Ahmed Al-Rashidi</div>
        <div className="text-[#64748b]">Riyadh, KSA</div>
      </div>
      <div className="mt-3 text-[#0f1b2d] font-semibold">To: The Visa Officer</div>
      <div className="text-[#64748b]">German Consulate</div>
      <div className="mt-3 space-y-1">
        <Line w="100%" />
        <Line w="92%" />
        <Line w="96%" />
        <Line w="88%" />
        <Line w="78%" />
      </div>
      <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
        <Row label="Applicant" value="A. AL-RASHIDI" />
        <Row label="Trip" value="15–22 SEP 2026" />
        <Row label="Covers" value="All expenses" />
      </div>
      <div className="mt-auto pt-2"><Badge>Signed</Badge></div>
    </div>
  );
}

function InvitationBody({ scale }) {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col" style={{ fontSize: scale }}>
      <div className="text-right">
        <div className="text-[#0f1b2d] font-semibold">Klaus Mueller</div>
        <div className="text-[#64748b]">Berlin, Germany</div>
      </div>
      <div className="mt-3 text-[#0f1b2d] font-semibold">To: The Visa Officer</div>
      <div className="text-[#64748b]">German Consulate</div>
      <div className="mt-3 space-y-1">
        <Line w="100%" />
        <Line w="94%" />
        <Line w="92%" />
        <Line w="80%" />
      </div>
      <div className="mt-3 space-y-1.5 border-t border-[#e2e8f0] pt-2">
        <Row label="Guest" value="A. AL-RASHIDI" />
        <Row label="Visit" value="15–22 SEP 2026" />
        <Row label="Stay" value="Host's home" />
      </div>
      <div className="mt-auto pt-2"><Badge>Confirmed</Badge></div>
    </div>
  );
}

/* ---- Tiny helpers ---- */
function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#64748b]">{label}</span>
      <span className={`text-[#0f1b2d] font-semibold ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
function Line({ w = "100%" }) { return <div style={{ width: w, height: 3, background: "#e2e8f0", borderRadius: 2 }} />; }
function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
      <CheckCircle2 className="w-2.5 h-2.5" /> {children}
    </span>
  );
}

/* ---- The PreviewCard itself ---- */

export function PreviewCard({ item, size = "full" }) {
  const compact = size === "thumb";
  const cardWidth = compact ? 150 : 220;
  const headerFontTitle = compact ? "text-[10px]" : "text-xs";
  const headerFontSub = compact ? "text-[9px]" : "text-[10px]";
  const bodyScale = compact ? "7px" : "10px";

  const rule = matchRule(item?.title || "");
  const Icon = rule?.icon || FileText;
  const BodyComp = rule?.body || null;

  return (
    <div
      style={{ width: cardWidth, flex: "0 0 auto" }}
      className="rounded-lg bg-white border border-[#e2e8f0] shadow-sm overflow-hidden flex flex-col"
      data-testid="preview-card"
    >
      {/* Header */}
      <div className={`bg-[#0f1b2d] text-white ${compact ? "px-2 py-1.5" : "px-3 py-2"}`}>
        <div className="flex items-center gap-1.5">
          <Icon className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"} flex-shrink-0`} />
          <div className={`font-display font-bold ${headerFontTitle} truncate`}>{item?.title || "Document"}</div>
        </div>
        {item?.package_contents ? (
          <div className={`${headerFontSub} text-white/65 truncate mt-0.5`}>{item.package_contents}</div>
        ) : null}
      </div>
      {/* A4 portrait body */}
      <div style={{ aspectRatio: "1 / 1.414" }} className="relative">
        {item?.example_file_url ? (
          <UploadedBody url={item.example_file_url} title={item?.title} />
        ) : BodyComp ? (
          <BodyComp scale={bodyScale} />
        ) : (
          <GenericBody title={item?.title || ""} />
        )}
      </div>
    </div>
  );
}

/* Generates default items (when package has no items array) - mostly unused now since defaults come from API */
export const ALL_MOCKUPS = [
  { key: "flight", label: "Flight Reservation", item: { title: "Flight Reservation with valid PNR", package_contents: "Confirmed Airline Ticket" } },
  { key: "hotel", label: "Hotel Booking", item: { title: "Hotel Booking Confirmation", package_contents: "Verified Booking.com reservation" } },
  { key: "insurance", label: "Travel Insurance", item: { title: "Travel Insurance (€30,000 / 30 days)", package_contents: "30,000 EUR coverage" } },
  { key: "cover", label: "Cover Letter", item: { title: "Cover Letter", package_contents: "Consulate-formatted letter" } },
  { key: "itinerary", label: "Travel Itinerary", item: { title: "Travel Itinerary & Day-by-Day Plan", package_contents: "Detailed day-by-day plan" } },
  { key: "car", label: "Car Rental", item: { title: "Car Rental Reservation", package_contents: "Major rental agency confirmation" } },
];

// Backward-compat exports (no longer used but kept to avoid breaking imports)
export const PACKAGE_TO_MOCKUPS = {
  basic: ["flight", "hotel"],
  complete: ["flight", "hotel", "car", "cover"],
  premium: ["flight", "hotel", "car", "cover", "itinerary", "insurance"],
};
